import { getAuthToken } from "../../api/client";

export const DEFAULT_APPAC_STUDIO_URL = "http://127.0.0.1:8088";

export type JobStatus = "queued" | "running" | "done" | "error";

export interface AppacHealth {
  ok: boolean;
  appac_commit: string;
  cuda: { available: boolean; device: string | null; id?: number; error?: string };
}

export interface WingStation {
  id: string;
  y: number;
  x_le: number;
  z_le: number;
  chord: number;
  twist_deg: number;
  airfoil: string;
}

export interface WingDefinition {
  schema_version: 1;
  name: string;
  symmetry: "half";
  chord_panels: number;
  span_panels_per_segment: number;
  stations: WingStation[];
}

export interface AppacCaseSpec {
  case: "custom-wing" | "sweptwing" | "bwb";
  wing?: WingDefinition;
  alpha: number;
  beta: number;
  backend: "lu" | "qr";
  max_iter: number;
  conv_tol: number;
  snapshot_interval: number;
}

export interface AppacJob {
  id: string;
  spec: AppacCaseSpec;
  cache_key: string;
  status: JobStatus;
  cached: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  latest_iteration: number;
  latest_residual: number | null;
  frame_count: number;
}

export interface AppacEvent {
  seq: number;
  at: string;
  level: "info" | "warning" | "error";
  message: string;
  iteration: number | null;
  residual: number | null;
  frame: number | null;
}

export interface AppacGeometry {
  points: number[][];
  cells: number[][];
  scalars?: number[];
  scalar_name?: string;
}

export interface AppacFrame extends AppacGeometry {
  body?: AppacGeometry;
  iteration: number;
  residual: number | null;
  index: number;
  count: number;
}

export interface AppacSweep {
  id: string;
  job_ids: string[];
  axes: Record<string, number[]>;
  created_at: string;
  states: Record<JobStatus, number>;
  jobs: AppacJob[];
}

export interface AppacCacheEntry extends AppacJob {
  size_bytes: number;
  appac_commit: string;
  result?: Record<string, number | boolean>;
}

export function appacStudioBaseUrl(): string {
  const configured = import.meta.env.VITE_APPAC_V100_URL;
  return (typeof configured === "string" && configured.trim()) || DEFAULT_APPAC_STUDIO_URL;
}

async function studioFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getAuthToken().catch(() => null);
  const headers = new Headers(options.headers);
  if (options.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(`${appacStudioBaseUrl().replace(/\/$/, "")}${path}`, {
    ...options,
    headers,
  });
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const payload = await response.json() as { detail?: string };
      if (payload.detail) detail = payload.detail;
    } catch {
      // Preserve the status fallback.
    }
    throw new Error(detail);
  }
  return response.json() as Promise<T>;
}

function wireCaseSpec(spec: AppacCaseSpec): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    case: spec.case,
    alpha: spec.alpha,
    beta: spec.beta,
    backend: spec.backend,
    max_iter: spec.max_iter,
    conv_tol: spec.conv_tol,
    snapshot_interval: spec.snapshot_interval,
  };
  if (spec.case === "custom-wing" && spec.wing) {
    payload.wing = {
      stations: spec.wing.stations,
      chord_panels: spec.wing.chord_panels,
      span_panels_per_segment: spec.wing.span_panels_per_segment,
    };
  }
  return payload;
}

export const appacApi = {
  health: () => studioFetch<AppacHealth>("/health"),
  jobs: () => studioFetch<{ jobs: AppacJob[] }>("/jobs"),
  job: (id: string) => studioFetch<AppacJob>(`/jobs/${encodeURIComponent(id)}`),
  events: (id: string, since = 0) =>
    studioFetch<{ events: AppacEvent[] }>(`/jobs/${encodeURIComponent(id)}/events?since=${since}`),
  frame: (id: string, index: number | "latest" = "latest") =>
    studioFetch<AppacFrame>(`/jobs/${encodeURIComponent(id)}/frames/${index}`),
  submit: (spec: AppacCaseSpec, force = false) =>
    studioFetch<AppacJob>("/jobs", {
      method: "POST",
      body: JSON.stringify({ ...wireCaseSpec(spec), force }),
    }),
  cache: () => studioFetch<{ entries: AppacCacheEntry[] }>("/cache"),
  submitSweep: (base: AppacCaseSpec, axes: Record<string, number[]>, force = false) =>
    studioFetch<AppacSweep>("/sweeps", {
      method: "POST",
      body: JSON.stringify({ base: wireCaseSpec(base), axes, force }),
    }),
  sweep: (id: string) => studioFetch<AppacSweep>(`/sweeps/${encodeURIComponent(id)}`),
};
